"use client"

import { useSyncExternalStore, useCallback } from "react"
import type { SectionSlug } from "@/lib/types/sections"
import type { UserTier } from "@/lib/stores/user-store"

export interface DeliverableVersion {
  id: string
  version: number
  generatedAt: string
  isLatest: boolean
  isOutdated: boolean
}

export interface DeliverableContent {
  coverPage: {
    title: string
    subtitle: string
    generatedDate: string
    tenantName: string
  }
  executiveSummary: string
  sectionDataSummary: string
  aiInsights: string[]
  strategyRecommendations: string[]
  nextSteps: string[]
  crossSectionReferences?: string[]
}

export interface Deliverable {
  id: string
  sectionSlug: SectionSlug
  tenantId: string
  title: string
  status: "generating" | "ready" | "error" | "outdated"
  versions: DeliverableVersion[]
  currentVersion: number
  generatedAt: string
  lastViewedAt?: string
  content?: DeliverableContent
}

export const DELIVERABLE_TITLES: Record<SectionSlug, string> = {
  identity: "Identity & Purpose Report",
  people: "People & Org Structure Report",
  offerings: "Product & Service Strategy Report",
  audience: "Audience Insight Report",
  brand: "Brand Strategy Blueprint",
  competition: "Competitive Landscape Report",
  technology: "Tech Stack & Optimization Report",
  marketing: "Marketing Strategy Overview",
  sales: "Sales Engine Blueprint",
  operations: "Operations & Process Report",
  finance: "Financial Strategy & Unit Economics Report",
  strategy: "Strategic Roadmap & Business Plan",
}

export const DELIVERABLE_PERMISSIONS: Record<
  UserTier,
  {
    canView: boolean
    canViewFull: boolean
    canDownloadPdf: boolean
    canShareWithTeam: boolean
    canSaveToFolder: boolean
    canWhiteLabel: boolean
    canCustomTemplate: boolean
  }
> = {
  anonymous: {
    canView: false,
    canViewFull: false,
    canDownloadPdf: false,
    canShareWithTeam: false,
    canSaveToFolder: false,
    canWhiteLabel: false,
    canCustomTemplate: false,
  },
  free: {
    canView: true,
    canViewFull: false,
    canDownloadPdf: false,
    canShareWithTeam: false,
    canSaveToFolder: false,
    canWhiteLabel: false,
    canCustomTemplate: false,
  },
  starter: {
    canView: true,
    canViewFull: true,
    canDownloadPdf: true,
    canShareWithTeam: false,
    canSaveToFolder: true,
    canWhiteLabel: false,
    canCustomTemplate: false,
  },
  team: {
    canView: true,
    canViewFull: true,
    canDownloadPdf: true,
    canShareWithTeam: true,
    canSaveToFolder: true,
    canWhiteLabel: true,
    canCustomTemplate: false,
  },
  agency: {
    canView: true,
    canViewFull: true,
    canDownloadPdf: true,
    canShareWithTeam: true,
    canSaveToFolder: true,
    canWhiteLabel: true,
    canCustomTemplate: false,
  },
  enterprise: {
    canView: true,
    canViewFull: true,
    canDownloadPdf: true,
    canShareWithTeam: true,
    canSaveToFolder: true,
    canWhiteLabel: true,
    canCustomTemplate: true,
  },
}

type DeliverableMap = Record<SectionSlug, Deliverable | null>

interface DeliverableState {
  deliverables: DeliverableMap
  activeDeliverable: SectionSlug | null
  showDeliverableModal: boolean
  pendingDeliverable: SectionSlug | null
}

const defaultDeliverables: DeliverableMap = {
  identity: null,
  people: null,
  offerings: null,
  audience: null,
  brand: null,
  competition: null,
  technology: null,
  marketing: null,
  sales: null,
  operations: null,
  finance: null,
  strategy: null,
}

let state: DeliverableState = {
  deliverables: { ...defaultDeliverables },
  activeDeliverable: null,
  showDeliverableModal: false,
  pendingDeliverable: null,
}

const listeners = new Set<() => void>()

function emitChange() {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return state
}

function getServerSnapshot(): DeliverableState {
  return {
    deliverables: { ...defaultDeliverables },
    activeDeliverable: null,
    showDeliverableModal: false,
    pendingDeliverable: null,
  }
}

// Actions
function generateDeliverable(sectionSlug: SectionSlug, tenantId: string) {
  const now = new Date().toISOString()
  const existingDeliverable = state.deliverables[sectionSlug]
  const newVersion = existingDeliverable ? existingDeliverable.currentVersion + 1 : 1

  state = {
    ...state,
    deliverables: {
      ...state.deliverables,
      [sectionSlug]: {
        id: `del_${sectionSlug}_${Date.now()}`,
        sectionSlug,
        tenantId,
        title: DELIVERABLE_TITLES[sectionSlug],
        status: "generating" as const,
        versions: [
          ...(existingDeliverable?.versions.map((v) => ({ ...v, isLatest: false })) || []),
          {
            id: `ver_${Date.now()}`,
            version: newVersion,
            generatedAt: now,
            isLatest: true,
            isOutdated: false,
          },
        ],
        currentVersion: newVersion,
        generatedAt: now,
      },
    },
    pendingDeliverable: sectionSlug,
  }
  emitChange()
}

function markDeliverableReady(sectionSlug: SectionSlug, content: DeliverableContent) {
  const deliverable = state.deliverables[sectionSlug]
  if (!deliverable) return

  state = {
    ...state,
    deliverables: {
      ...state.deliverables,
      [sectionSlug]: { ...deliverable, status: "ready" as const, content },
    },
    showDeliverableModal: true,
    pendingDeliverable: sectionSlug,
  }
  emitChange()
}

function closeDeliverableModal() {
  state = { ...state, showDeliverableModal: false, pendingDeliverable: null }
  emitChange()
}

export function useDeliverableStore() {
  const currentState = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  return {
    deliverables: currentState.deliverables,
    activeDeliverable: currentState.activeDeliverable,
    showDeliverableModal: currentState.showDeliverableModal,
    pendingDeliverable: currentState.pendingDeliverable,
    generateDeliverable: useCallback(generateDeliverable, []),
    markDeliverableReady: useCallback(markDeliverableReady, []),
    closeDeliverableModal: useCallback(closeDeliverableModal, []),
    getDeliverable: useCallback((slug: SectionSlug) => currentState.deliverables[slug], [currentState.deliverables]),
    hasDeliverable: useCallback(
      (slug: SectionSlug) => currentState.deliverables[slug]?.status === "ready",
      [currentState.deliverables],
    ),
  }
}
